const DEFAULT_TIMEOUT_MS = 5000;

/**
 * @typedef {Record<string, boolean>} FeatureFlags
 * @typedef {{ rum?: Record<string, unknown>, record?: Record<string, unknown> }} HostConfig
 * @typedef {{ features: FeatureFlags, config: HostConfig }} HostEntry
 * @typedef {Record<string, HostEntry>} Manifest
 */

/**
 * Normalize a hostname for lookup (lowercase, trim).
 * @param {string} [hostname]
 * @returns {string}
 */
export function normalizeHostname(hostname) {
  if (!hostname || typeof hostname !== 'string') return '';
  return hostname.trim().toLowerCase();
}

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Legacy host value: flat feature flags only, e.g. `{ "record": true }`.
 * @param {unknown} value
 * @returns {value is FeatureFlags}
 */
function isLegacyFeatureFlags(value) {
  if (!isPlainObject(value)) return false;
  if ('features' in value || 'config' in value) return false;
  const values = Object.values(value);
  if (values.length === 0) return false;
  return values.every((v) => typeof v === 'boolean');
}

/**
 * @param {unknown} value
 * @returns {value is FeatureFlags}
 */
function isFeatureFlags(value) {
  if (!isPlainObject(value)) return false;
  return Object.values(value).every((v) => typeof v === 'boolean');
}

/**
 * Default resolved entry when host is missing or invalid.
 * RUM config is always present; record config is omitted.
 * @returns {HostEntry}
 */
export function emptyHostEntry() {
  return {
    features: {},
    config: {
      rum: {},
    },
  };
}

/**
 * Normalize a raw host value into `{ features, config }`.
 *
 * Supported shapes:
 * - Legacy: `{ "record": true }`
 * - New:
 *   {
 *     "features": { "record": true },
 *     "config": { "rum": { ... }, "record": { ... } }
 *   }
 *
 * Rules:
 * - `config.rum` is always present (RUM is default/on)
 * - `config.record` is only present when `features.record === true`
 *
 * @param {unknown} value
 * @returns {HostEntry | null}
 */
export function normalizeHostEntry(value) {
  if (!isPlainObject(value)) return null;

  /** @type {FeatureFlags} */
  let features = {};
  /** @type {Record<string, unknown>} */
  let rawConfig = {};

  if (isLegacyFeatureFlags(value)) {
    features = { ...value };
  } else if ('features' in value || 'config' in value) {
    if (value.features != null && !isFeatureFlags(value.features)) {
      return null;
    }
    if (value.config != null && !isPlainObject(value.config)) {
      return null;
    }
    features = isFeatureFlags(value.features) ? { ...value.features } : {};
    rawConfig = isPlainObject(value.config) ? value.config : {};
  } else {
    return null;
  }

  /** @type {HostConfig} */
  const config = {
    rum:
      isPlainObject(rawConfig.rum) ? { ...rawConfig.rum } : {},
  };

  // Record configs only when the record feature is enabled
  if (features.record === true) {
    config.record = isPlainObject(rawConfig.record)
      ? { ...rawConfig.record }
      : {};
  }

  return { features, config };
}

/**
 * Look up a raw host entry from the manifest (exact, then without www.).
 * @param {Manifest | Record<string, unknown> | null | undefined} manifest
 * @param {string} host
 * @returns {unknown}
 */
function lookupRawHostEntry(manifest, host) {
  if (!manifest || !host) return undefined;

  if (Object.prototype.hasOwnProperty.call(manifest, host)) {
    return manifest[host];
  }

  if (host.startsWith('www.')) {
    const bare = host.slice(4);
    if (Object.prototype.hasOwnProperty.call(manifest, bare)) {
      return manifest[bare];
    }
  }

  return undefined;
}

/**
 * Resolve features + config for the current site hostname.
 *
 * Preference order:
 * 1. Exact match manifest[hostname]
 * 2. Same host without leading "www."
 * 3. Empty entry (core RUM only, rum config `{}`, no record config)
 *
 * @param {Manifest | Record<string, unknown> | null | undefined} manifest
 * @param {string} [hostname] defaults to location.hostname in the browser
 * @returns {HostEntry}
 */
export function resolveHostForHostname(manifest, hostname) {
  const host = normalizeHostname(
    hostname != null
      ? hostname
      : typeof location !== 'undefined'
        ? location.hostname
        : '',
  );

  if (!manifest || typeof manifest !== 'object' || !host) {
    return emptyHostEntry();
  }

  const raw = lookupRawHostEntry(manifest, host);
  const normalized = normalizeHostEntry(raw);
  return normalized || emptyHostEntry();
}

/**
 * Resolve feature flags for the current site hostname.
 * Kept for callers that only need the boolean feature map.
 *
 * @param {Manifest | Record<string, unknown> | null | undefined} manifest
 * @param {string} [hostname] defaults to location.hostname in the browser
 * @returns {FeatureFlags}
 */
export function resolveFeaturesForHost(manifest, hostname) {
  return { ...resolveHostForHostname(manifest, hostname).features };
}

/**
 * Fetch and lightly validate a client manifest.
 * Soft-fails to an empty object on any error.
 *
 * @param {string} manifestUrl
 * @param {{ timeoutMs?: number }} [options]
 * @returns {Promise<Manifest>}
 */
export async function fetchManifest(manifestUrl, options = {}) {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  try {
    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;

    const response = await fetch(manifestUrl, {
      method: 'GET',
      credentials: 'omit',
      signal: controller ? controller.signal : undefined,
    });

    if (timer) clearTimeout(timer);

    if (!response.ok) {
      return {};
    }

    const data = await response.json();
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return {};
    }

    /** @type {Manifest} */
    const hosts = {};
    for (const [key, value] of Object.entries(data)) {
      // Skip accidental metadata keys if present
      if (key === 'version' || key === 'hosts' || key === 'features') {
        continue;
      }
      const normalized = normalizeHostEntry(value);
      if (normalized) {
        hosts[normalizeHostname(key)] = normalized;
      }
    }

    return hosts;
  } catch {
    return {};
  }
}
